import { BridgeAsync, BridgeEvents, BridgeSync } from '../Bridges/Bridges';
import { callNative, callNativeAsync, registerNative, ValueType } from './CallNative';
import * as EventEmitter from 'eventemitter3';

type EventListener<TBridgeName extends keyof BridgeSync | keyof BridgeAsync | keyof BridgeEvents> = <
    TEventName extends keyof ValueType<TBridgeName, BridgeEvents>
>(
    name: TEventName,
    callback: ValueType<TEventName, ValueType<TBridgeName, BridgeEvents>>
) => void;

export function generateBridge<
    TBridgeName extends keyof BridgeSync | keyof BridgeAsync | keyof BridgeEvents,
    TBridgeSync extends ValueType<TBridgeName, BridgeSync>,
    TBridgeAsync extends ValueType<TBridgeName, BridgeAsync>,
    TBridgeEvent extends ValueType<TBridgeName, BridgeEvents>
>({
    name,
    sync,
    async,
    event,
}: {
    name: TBridgeName;
    sync: TBridgeSync;
    async: TBridgeAsync;
    event: TBridgeEvent;
}): ValueType<TBridgeName, BridgeSync> &
    ValueType<TBridgeName, BridgeAsync> & {
        on: EventListener<TBridgeName>;
        once: EventListener<TBridgeName>;
        off: EventListener<TBridgeName>;
        removeAllListeners: VoidFunction;
    } {
    const generated: any = {};

    Object.keys(sync || {}).forEach((key) => {
        generated[key] = (...args: unknown[]) => callNative(name as any, key, ...args);
    });

    Object.keys(async || {}).forEach((key) => {
        generated[key] = (...args: unknown[]) => callNativeAsync(name as any, key, ...args);
    });

    const eventEmitter = new EventEmitter();
    generated.on = eventEmitter.on;
    generated.once = eventEmitter.once;
    generated.off = eventEmitter.off;
    generated.removeAllListeners = eventEmitter.removeAllListeners;
    Object.keys(event || {}).forEach((key) => {
        registerNative(name as any, key, (...args: any) => eventEmitter.emit(key, ...args));
    });

    return generated;
}
